import { useAuthStore } from '@/stores/auth.js'
import { usePermissions } from '@/composables/usePermissions.js'

// Routes that never require authentication
const PUBLIC_ROUTE_NAMES = new Set([
  'Login',
  'TenantSlugLogin',
  'TenantOidc',
  'OidcCallback',
  'StepUp',
  'NotFound',
])

/**
 * authGuard — called by router.beforeEach.
 *
 * 1. Lets public routes through immediately.
 * 2. If not authenticated, attempts a silent token refresh (uses the
 *    HttpOnly refresh cookie).  If that fails, redirects to /login.
 * 3. Enforces adminOnly meta on routes that require it.
 * 4. Enforces requiredPermission meta for role-gated routes
 *    (isAdmin | isManagerOrAbove | isOperatorOrAbove | isBackOffice |
 *     canManageLocations).
 *
 * Returns `true` to continue navigation or a route descriptor to redirect.
 */
export async function authGuard(to) {
  if (to.meta?.public || PUBLIC_ROUTE_NAMES.has(to.name)) return true

  const authStore = useAuthStore()

  if (!authStore.isAuthenticated) {
    try {
      await authStore.refreshToken()
    } catch {
      return { name: 'Login', query: { redirect: to.fullPath } }
    }
  }

  // Permission checks only once the store has loaded them
  if (authStore.permissions !== null) {
    const {
      isAdmin,
      isManagerOrAbove,
      isOperatorOrAbove,
      isBackOffice,
      canManageLocations,
    } = usePermissions()

    if (to.meta?.adminOnly && !isAdmin.value) {
      console.warn('[authGuard] admin role required for', to.path)
      return { path: '/' }
    }

    const requiredPermission = to.meta?.requiredPermission
    if (requiredPermission) {
      const permMap = {
        isAdmin: isAdmin.value,
        isManagerOrAbove: isManagerOrAbove.value,
        isOperatorOrAbove: isOperatorOrAbove.value,
        isBackOffice: isBackOffice.value,
        canManageLocations: canManageLocations.value,
      }
      if (!(requiredPermission in permMap)) {
        console.error('[authGuard] Unknown requiredPermission key:', requiredPermission, 'on route', to.path)
        return { path: '/' }
      }
      if (!permMap[requiredPermission]) {
        console.warn('[authGuard]', requiredPermission, 'required for', to.path)
        return { path: '/' }
      }
    }
  }

  return true
}
